import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import * as Sentry from '@sentry/react'
import { ErrorBoundary } from './components/ErrorBoundary'
import App from './App'
import './index.css'

// Initialize Sentry for error tracking (only if DSN is configured)
console.log('[Sentry] DSN configured:', !!import.meta.env.VITE_SENTRY_DSN)
if (import.meta.env.VITE_SENTRY_DSN) {
  console.log('[Sentry] Initializing...')
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,

    // Capture 100% of errors (adjust in production if needed)
    sampleRate: 1.0,

    // Performance monitoring - capture 10% of transactions
    tracesSampleRate: 0.1,

    // Enable React-specific integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Capture session replays on error
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Session replay - capture 10% of sessions, 100% on error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Add useful context to errors
    beforeSend(event) {
      // Add current route to error context
      event.tags = event.tags || {}
      event.tags.route = window.location.pathname
      event.tags.url = window.location.href
      return event
    },
  })
  console.log('[Sentry] Initialized successfully')
} else {
  console.log('[Sentry] Skipped - no DSN configured')
}

// Global handler for chunk load errors (stale deployments)
// This catches errors that happen outside of React component lifecycle
const CHUNK_RELOAD_KEY = 'chunk_reload_attempt'
const CHUNK_RELOAD_TIMEOUT = 10000 // 10 seconds window

function isChunkLoadError(error) {
  const message = error?.message || error?.toString() || ''
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('ChunkLoadError')
  )
}

function handleChunkError() {
  const lastAttempt = sessionStorage.getItem(CHUNK_RELOAD_KEY)
  const now = Date.now()

  if (lastAttempt && now - parseInt(lastAttempt, 10) < CHUNK_RELOAD_TIMEOUT) {
    console.warn('Chunk reload attempted recently, not auto-reloading')
    return
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, now.toString())
  console.log('Auto-reloading due to stale chunk...')
  window.location.reload()
}

// Listen for unhandled promise rejections (dynamic imports)
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault()
    handleChunkError()
  }
})

// Listen for regular errors
window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.error)) {
    event.preventDefault()
    handleChunkError()
  }
})

// Query client configuration - optimized for instant-feeling UI
// gcTime raised to 24h so the persister has something to rehydrate from on cold start.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 24 * 60 * 60 * 1000, // 24h — persisted cache survives reloads
      // Don't refetch every time the user switches tabs — with 13+ queries
      // on the dashboard this triggers a 13-request burst on every tab focus.
      // Realtime subscriptions + refetchInterval keep data fresh without it.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
    },
  },
})

// Persist the React Query cache to localStorage so AdsManager / Dashboard open
// with last-seen data instantly on reload, then refetch in the background.
// Versioning via `buster` lets us invalidate everything after an API shape change.
const queryPersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: '99d_rq_cache',
  throttleTime: 2000,
})

// Today's KPI data is always stale the moment a new session opens (new day,
// new calls, new spend). Persisting it produces a "number jump" where the card
// shows yesterday's cached value for ~1s before the background refetch lands.
// Excluding these keys means KPI cards show a skeleton on cold load and render
// correctly once — no visible jump. Semi-static data (accounts, landers) keeps
// persisting for instant load.
const VOLATILE_QUERY_KEYS = new Set([
  // V3 bundled bootstrap (replaces v3-adpos/v3-spendgeo/v3-top-performers
  // and the unified todayDashboard/profitSummary keys for V3's path).
  'v3-bootstrap',
  // Mobile + V2 still use these individually.
  'todayDashboard', 'profitSummary',
  'v3-raw-clicks', 'v3-ctc',
  'v3-ad-accounts-overview',
  'liveCalls',
])

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: queryPersister,
          maxAge: 4 * 60 * 60 * 1000, // 4h — today-KPIs excluded via shouldDehydrateQuery
          buster: import.meta.env.VITE_COMMIT_SHA || 'v1',
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => !VOLATILE_QUERY_KEYS.has(query.queryKey[0]),
          },
        }}
      >
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
      </PersistQueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
)
